import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "./index.css";
import "./styles/talmia.css";
import "./styles/light-sales.css";
import "./styles/light-universal.css";
import "./styles/headers.css";
import App from "./App";
import { ThemeProvider } from "./context/ThemeContext";

// Benign "ResizeObserver loop …" warnings (fired by virtualized tables on
// resize/scroll) must not trip CRA's dev error overlay. Swallow just that
// message so it never surfaces as a full-screen "Uncaught runtime error".
const _roErrRe = /ResizeObserver loop (limit exceeded|completed with undelivered notifications)/;

// Root-cause fix: defer ResizeObserver callbacks to the next frame so the
// synchronous observe→resize→observe loop (virtualized tables) never throws.
if (typeof window !== "undefined" && window.ResizeObserver) {
  const _NativeRO = window.ResizeObserver;
  window.ResizeObserver = class extends _NativeRO {
    constructor(callback) {
      super((entries, observer) => {
        window.requestAnimationFrame(() => {
          try { callback(entries, observer); } catch (_) { /* swallow benign RO throw */ }
        });
      });
    }
  };
}

window.addEventListener("error", (e) => {
  if (e?.message && _roErrRe.test(e.message)) {
    e.stopImmediatePropagation();
    e.preventDefault();
  }
}, true);
window.addEventListener("unhandledrejection", (e) => {
  const msg = e?.reason?.message || "";
  if (_roErrRe.test(msg)) { e.stopImmediatePropagation(); e.preventDefault(); }
}, true);

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 60_000, refetchOnWindowFocus: false } },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <App />
      </ThemeProvider>
    </QueryClientProvider>
  </React.StrictMode>
);
